import React, { useState } from "react";
import SaveUserEvent from "../../actions/SaveUserEvent";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { ChevronDown } from "lucide-react";

const SyllabusAccordion = ({ chapters, mode }) => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [expand, setExpand] = useState(false);
  const [opened, setOpened] = useState(false);

  const { user } = useContext(AuthContext);

  const OpenAccordion = () => {
    setOpened(true);
    if (opened) {
      SaveUserEvent({
        payload: {
          session: user.SessionId,
          page: window.location.pathname,
          action: "SyllabusAccordion-opened",
        },
      });
    }
  };

  const toggleAccordion = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
    OpenAccordion();
  };

  const displayedChapters = expand ? chapters : chapters.slice(0, 6);

  return (
    <div className="syllabus-container">
      <style>{`
        .syllabus-wrapper {
          width: 75%;
          max-width: 1200px;
          margin: 0 auto 60px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 20px;
        }

        .accordion-item-wrapper {
          background: #ffffff;
          border: 2px solid #e0e0e0;
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .accordion-item-wrapper:hover {
          border-color: #d6e4ff;
          background: #fafbff;
        }

        .accordion-item-wrapper.active {
          border-color: #e99b3c;
          background: #f0f4ff;
        }

        .accordion-header-button {
          width: 100%;
          padding: 22px 28px;
          background: transparent;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 16px;
          text-align: left;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .accordion-header-button:hover {
          background: transparent;
        }

        .accordion-header-button.active {
          background: transparent;
        }

        .accordion-icon {
          width: 28px;
          height: 28px;
          min-width: 28px;
          color: #e08d21;
          transition: transform 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .accordion-header-button.active .accordion-icon {
          transform: rotate(180deg);
        }

        .accordion-header-title {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
          flex: 1;
        }

        @media (max-width: 768px) {
          .accordion-header-title {
            font-size: 16px;
          }

          .accordion-header-button {
            padding: 20px 24px;
            gap: 12px;
          }

          .accordion-icon {
            width: 24px;
            height: 24px;
          }
        }

        @media (max-width: 576px) {
          .accordion-header-button {
            padding: 18px 20px;
          }

          .accordion-header-title {
            font-size: 15px;
          }

          .accordion-icon {
            width: 22px;
            height: 22px;
          }

          .syllabus-wrapper {
            padding: 0 12px;
            gap: 14px;
          }
        }

        .accordion-body-wrapper {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
          background: transparent;
          border-top: 1px solid #999999;
        }

        .accordion-body-wrapper.active {
          max-height: 1500px;
        }

        .accordion-body-content {
          padding: 16px 28px 24px 72px;
        }

        @media (max-width: 576px) {
          .accordion-body-content {
            padding: 12px 20px 16px 52px;
          }
        }

        .topic-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 0;
          color: #333333;
          font-size: 15px;
          line-height: 1.6;
          transition: all 0.2s ease;
        }

        .topic-item:hover {
          color: #e8893c;
        }

        .topic-icon {
          width: 18px;
          height: 18px;
          min-width: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 2px;
        }

        .topic-text {
          color: #333333;
          font-size: 15px;
          font-weight: 500;
        }

        .topic-item:hover .topic-text {
          color: #ec881e;
        }

        .more-chapters-btn {
          max-width: 280px;
          margin: 40px auto 0;
          padding: 14px 32px;
          background: #ffffff;
          border: 2px solid #dd832f;
          border-radius: 12px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 700;
          color: #e79933;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .more-chapters-btn:hover {
          background: #dfa246;
          color: #ffffff;
          transform: translateY(-2px);
        }

        @media (max-width: 576px) {
          .more-chapters-btn {
            max-width: 100%;
            padding: 12px 24px;
            font-size: 15px;
            margin-top: 32px;
          }
        }

        .section-locked {
          background: #f0f9f7;
          border-color: #b8e0d8;
        }

        .section-locked:hover {
          border-color: #a8d8d0;
        }

        .section-locked .accordion-header-title {
          color: #1d7d6e;
        }

        .section-locked .accordion-icon {
          color: #1d7d6e;
        }
      `}</style>

      <div className="syllabus-wrapper">
        {displayedChapters.map((chapter, index) => (
          <div
            key={index}
            className={`accordion-item-wrapper ${
              expandedIndex === index ? "active" : ""
            } ${index >= 6 && !expand ? "section-locked" : ""}`}
          >
            <button
              className={`accordion-header-button ${
                expandedIndex === index ? "active" : ""
              }`}
              onClick={() => toggleAccordion(index)}
            >
              <div className="accordion-icon">
                <ChevronDown size={24} strokeWidth={2.5} />
              </div>
              <h4 className="accordion-header-title fs-5 fw-bold">
                {chapter.chapter_title}
              </h4>
            </button>

            <div
              className={`accordion-body-wrapper ${
                expandedIndex === index ? "active" : ""
              }`}
            >
              <div className="accordion-body-content">
                {chapter.topics && chapter.topics.length > 0 ? (
                  <div>
                    {chapter.topics.map((topic, topicIndex) => (
                      <div key={topicIndex} className="topic-item">
                        <div className="topic-icon">📗</div>
                        <span className="topic-text fs-6">
                          {topic.topic_title}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}

        {chapters.length > 6 && !expand && (
          <button className="more-chapters-btn" onClick={() => setExpand(true)}>
            View More Chapters
          </button>
        )}

        {chapters.length > 6 && expand && (
          <button
            className="more-chapters-btn"
            onClick={() => setExpand(false)}
          >
            View Less
          </button>
        )}
      </div>
    </div>
  );
};

export default SyllabusAccordion;